/**
 * Cart Drawer Component
 *
 * Slide-out drawer that shows cart contents.
 * Opens from the right on desktop, full screen on mobile.
 */

import { Fragment, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link } from 'react-router';
import { X, ShoppingBag, Trash2, AlertTriangle, Repeat } from 'lucide-react';
import { useCartStore } from '@/stores/useCartStore';
import { useRemoveCartItem, useUpdateCartItem } from '@/hooks/useCart';
import type { CartItem } from '@/types/cart';
import { Button } from '@/components/ui/button';
import { useFormatPrice } from '@/hooks/useFormatPrice';
import { EmptyCart } from './EmptyCart';
import { QuantitySelector } from './QuantitySelector';
import { trackCartEvent } from '@/lib/telemetry/cartEvents';

/**
 * Get monthly rental duration label
 */
const getMonthlyRentalLabel = (months: number | undefined): string => {
    if (!months) return 'Monthly Rental';
    return `${months} month rental`;
};

/**
 * Calculate item total
 */
const getItemTotal = (item: CartItem): number => {
    // Use pre-calculated totalPrice if available and valid
    if (item.totalPrice !== undefined && item.totalPrice > 0) {
        return item.totalPrice;
    }

    // For subscriptions, calculate from monthly rate
    if (item.isSubscription) {
        return (item.subscriptionMonthlyRate ?? item.unitPrice ?? 0) * item.quantity;
    }

    // For rentals, return whatever totalPrice is (even if 0, API should have calculated)
    return item.totalPrice || 0;
};

export const CartDrawer = () => {
    const formatPrice = useFormatPrice();
    const {
        items,
        subtotal,
        totalDeposit,
        grandTotal,
        isDrawerOpen,
        closeDrawer,
        isLoading,
        isValidating,
        validationErrors,
        hasValidationErrors,
        isLocked,
    } = useCartStore();

    // API-backed mutations for cart operations
    const removeCartItemMutation = useRemoveCartItem();
    const updateCartItemMutation = useUpdateCartItem();

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    // Track drawer open/close with dwell time
    const drawerOpenedAt = useRef<number | null>(null);

    useEffect(() => {
        if (isDrawerOpen) {
            drawerOpenedAt.current = Date.now();
            trackCartEvent.drawerOpened(itemCount, 'header');
        } else if (drawerOpenedAt.current) {
            const dwellTime = Date.now() - drawerOpenedAt.current;
            trackCartEvent.drawerClosed(itemCount, dwellTime);
            drawerOpenedAt.current = null;
        }
    }, [isDrawerOpen, itemCount]);

    return (
        <Transition.Root show={isDrawerOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={closeDrawer}>
                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-foreground/40 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
							<Transition.Child
								as={Fragment}
								enter="transform transition [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] duration-350"
								enterFrom="translate-x-full"
								enterTo="translate-x-0"
								leave="transform transition [transition-timing-function:cubic-bezier(0.4,0,1,1)] duration-250"
								leaveFrom="translate-x-0"
								leaveTo="translate-x-full"
							>
                                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                                    <div className="flex h-full flex-col bg-white shadow-xl">
                                        {/* Header */}
                                        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
                                            <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-foreground">
                                                <ShoppingBag className="h-5 w-5" />
                                                Shopping Cart
                                                {itemCount > 0 && (
                                                    <span
                                                        key={itemCount}
                                                        className="delight-pop ml-2 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-white"
                                                    >
                                                        {itemCount}
                                                    </span>
                                                )}
                                            </Dialog.Title>
                                            <button
                                                type="button"
                                                onClick={closeDrawer}
                                                className="text-muted-foreground/70 hover:text-muted-foreground transition-colors"
                                            >
                                                <span className="sr-only">Close</span>
                                                <X className="h-6 w-6" />
                                            </button>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 overflow-y-auto px-4 py-4">
                                            {isLoading ? (
                                                <div className="flex items-center justify-center h-32">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                                                </div>
                                            ) : items.length === 0 ? (
                                                <EmptyCart onClose={closeDrawer} />
                                            ) : (
                                                <ul className="divide-y divide-border">
                                                    {items.map((item, idx) => (
                                                        <li
                                                            key={item.id}
                                                            className={`py-4 delight-fade-in`}
                                                            style={{ animationDelay: `${Math.min(idx * 60, 300)}ms` }}
                                                        >
                                                            <div className="flex gap-4">
                                                                {/* Product Image */}
                                                                <div className="flex-shrink-0 w-20 h-20 bg-muted rounded-md overflow-hidden">
                                                                    {item.productImage ? (
                                                                        <img
                                                                            src={item.productImage}
                                                                            alt={item.productName}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    ) : (
                                                                        <div className="w-full h-full flex items-center justify-center">
                                                                            <ShoppingBag className="h-8 w-8 text-muted-foreground/50" />
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Product Details */}
                                                                <div className="flex-1 min-w-0">
                                                                    <h4 className="text-sm font-medium text-foreground line-clamp-2">
                                                                        {item.productName}
                                                                    </h4>

                                                                    {/* Duration: Monthly Rental or Date Range */}
                                                                    {item.isSubscription ? (
                                                                        <p className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                                                                            <Repeat className="h-3 w-3" />
                                                                            {getMonthlyRentalLabel(item.subscriptionMonths)}
                                                                        </p>
                                                                    ) : item.startDate && item.endDate ? (
                                                                        <p className="mt-1 text-xs text-muted-foreground">
                                                                            {new Date(item.startDate).toLocaleDateString()} -{' '}
                                                                            {new Date(item.endDate).toLocaleDateString()}
                                                                        </p>
                                                                    ) : null}

                                                                    {/* Quantity and Price Row */}
                                                                    <div className="mt-2 flex items-center justify-between">
                                                                        <QuantitySelector
                                                                            value={item.quantity}
                                                                            onChange={(qty: number) =>
                                                                                updateCartItemMutation.mutate({ itemId: item.id, quantity: qty })
                                                                            }
                                                                            min={1}
                                                                            max={10}
                                                                            size="sm"
                                                                            disabled={isLocked || updateCartItemMutation.isPending}
                                                                        />
                                                                        <div className="text-right">
                                                                            <p className="text-sm font-medium text-foreground">
                                                                                {formatPrice(getItemTotal(item))}
                                                                                {item.isSubscription && (
                                                                                    <span className="text-xs text-muted-foreground font-normal">/mo</span>
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Remove Button — hidden when cart is locked */}
                                                                {!isLocked && (
                                                                    <button
                                                                        onClick={() => removeCartItemMutation.mutate(item.id)}
                                                                        disabled={removeCartItemMutation.isPending}
                                                                        className="flex-shrink-0 p-1 text-muted-foreground/70 hover:text-destructive transition-colors disabled:opacity-50"
                                                                        aria-label={`Remove ${item.productName} from cart`}
                                                                    >
                                                                        <Trash2 className="h-5 w-5" aria-hidden="true" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>

                                        {/* Validation Warnings */}
                                        {isValidating && (
                                            <div className="px-4 py-3 bg-info-subtle border-t border-info-border">
                                                <div className="flex items-center gap-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-info" />
                                                    <p className="text-sm text-info">
                                                        Checking availability for new dates/location...
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        {!isValidating && hasValidationErrors() && validationErrors.length > 0 && (
                                            <div className="px-4 py-3 bg-warning-subtle border-t border-warning-border">
                                                <div className="flex items-start gap-2 mb-2">
                                                    <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
                                                    <p className="text-sm font-medium text-warning">
                                                        Some items need attention
                                                    </p>
                                                </div>
                                                <ul className="space-y-2">
                                                    {validationErrors.map((err, index) => (
                                                        <li
                                                            key={`${err.itemId}-${index}`}
                                                            className="text-xs text-warning bg-warning-subtle rounded px-2 py-1.5"
                                                        >
                                                            <span className="font-medium">{err.equipmentName}:</span>{' '}
                                                            {err.error}
                                                            {err.availableQuantity !== undefined && err.requestedQuantity !== undefined && (
                                                                <span className="block text-warning mt-0.5">
                                                                    Only {err.availableQuantity} available (you requested {err.requestedQuantity})
                                                                </span>
                                                            )}
                                                            {err.priceChanged && err.newPrice !== undefined && (
                                                                <span className="block text-warning mt-0.5">
                                                                    New price: {formatPrice(err.newPrice)}/day
                                                                </span>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {/* Footer with totals and actions */}
                                        {items.length > 0 && (
                                            <div className="border-t border-border px-4 py-4">
                                                {/* Subscription Billing Notice */}
                                                {items.some(item => item.isSubscription) && (
                                                    <div className="mb-3 pb-3 border-b border-border">
                                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                            <Repeat className="h-3 w-3" />
                                                            Subscription items billed monthly after checkout
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Pricing Summary */}
                                                <div className="space-y-2 mb-4">
                                                    <div className="flex justify-between text-sm text-muted-foreground">
                                                        <span>Subtotal</span>
                                                        <span>{formatPrice(subtotal)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-sm text-muted-foreground">
                                                        <span>Refundable Deposit</span>
                                                        <span>{formatPrice(totalDeposit)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-base font-semibold text-foreground pt-2 border-t border-border">
                                                        <span>Total</span>
                                                        <span>{formatPrice(grandTotal)}</span>
                                                    </div>
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="space-y-2">
                                                    {hasValidationErrors() ? (
                                                        <Button className="w-full" size="lg" disabled>
                                                            Resolve Issues to Checkout
                                                        </Button>
                                                    ) : (
                                                        <Link
                                                            to="/checkout"
                                                            onClick={closeDrawer}
                                                            className="block"
                                                        >
                                                            <Button className="w-full" size="lg">
                                                                Checkout
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    <Link
                                                        to="/cart"
                                                        onClick={closeDrawer}
                                                        className="block"
                                                    >
                                                        <Button
                                                            variant="outline"
                                                            className="w-full"
                                                        >
                                                            View Cart
                                                        </Button>
                                                    </Link>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default CartDrawer;
